import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Chip,
  Divider,
  Grid,
  Paper,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { formatDateTime } from "./utils";

const getStatusColor = (status) => {
  switch (status?.toLowerCase()) {
    case "done":
    case "completed":
    case "success":
      return "success";
    case "running":
    case "processing":
      return "warning";
    case "failed":
    case "error":
      return "error";
    default:
      return "default";
  }
};

const FieldRow = ({ label, value, mono = false }) => (
  <Box sx={{ mb: 1 }}>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography
      variant="body2"
      sx={mono ? { fontFamily: "monospace", fontSize: "0.8rem" } : {}}
    >
      {value ?? "—"}
    </Typography>
  </Box>
);

const StepBlock = ({ title, data, t }) => {
  if (!data) return null;

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: "bold" }}>
        {title}
      </Typography>
      <Grid container spacing={2}>
        {data.startedAt && (
          <Grid item xs={6} sm={4}>
            <FieldRow label={t("pages.logs.modal.startedAt")} value={formatDateTime(data.startedAt)} />
          </Grid>
        )}
        {data.finishedAt && (
          <Grid item xs={6} sm={4}>
            <FieldRow label={t("pages.logs.modal.finishedAt")} value={formatDateTime(data.finishedAt)} />
          </Grid>
        )}
        {data.fetchedCount !== undefined && (
          <Grid item xs={6} sm={4}>
            <FieldRow label={t("pages.logs.modal.fetchedCount")} value={data.fetchedCount} />
          </Grid>
        )}
        {data.importedCount !== undefined && (
          <Grid item xs={6} sm={4}>
            <FieldRow label={t("pages.logs.modal.importedCount")} value={data.importedCount} />
          </Grid>
        )}
        {data.imageCandidatesCount !== undefined && (
          <Grid item xs={6} sm={4}>
            <FieldRow label={t("pages.logs.modal.imageCandidates")} value={data.imageCandidatesCount} />
          </Grid>
        )}
        {data.productsCreated !== undefined && (
          <Grid item xs={6} sm={4}>
            <FieldRow label={t("pages.logs.modal.productsCreated")} value={data.productsCreated} />
          </Grid>
        )}
        {data.productsUpdated !== undefined && (
          <Grid item xs={6} sm={4}>
            <FieldRow label={t("pages.logs.modal.productsUpdated")} value={data.productsUpdated} />
          </Grid>
        )}
        {data.productsSkipped !== undefined && (
          <Grid item xs={6} sm={4}>
            <FieldRow
              label={t("pages.logs.modal.productsSkipped")}
              value={Number.isNaN(data.productsSkipped) ? "N/A" : data.productsSkipped}
            />
          </Grid>
        )}
        {data.categoriesCreated !== undefined && (
          <Grid item xs={6} sm={4}>
            <FieldRow label={t("pages.logs.modal.categoriesCreated")} value={data.categoriesCreated} />
          </Grid>
        )}
        {data.menusCreated !== undefined && (
          <Grid item xs={6} sm={4}>
            <FieldRow label={t("pages.logs.modal.menusCreated")} value={data.menusCreated} />
          </Grid>
        )}
        {data.totalUploaded !== undefined && (
          <Grid item xs={6} sm={4}>
            <FieldRow label={t("pages.logs.modal.totalUploaded")} value={data.totalUploaded} />
          </Grid>
        )}
        {data.totalProcessed !== undefined && (
          <Grid item xs={6} sm={4}>
            <FieldRow label={t("pages.logs.modal.totalProcessed")} value={data.totalProcessed} />
          </Grid>
        )}
        {data.totalFailed !== undefined && (
          <Grid item xs={6} sm={4}>
            <FieldRow label={t("pages.logs.modal.totalFailed")} value={data.totalFailed} />
          </Grid>
        )}
        {data.totalSkipped !== undefined && (
          <Grid item xs={6} sm={4}>
            <FieldRow label={t("pages.logs.modal.totalSkipped")} value={data.totalSkipped} />
          </Grid>
        )}
        {data.iterations !== undefined && (
          <Grid item xs={6} sm={4}>
            <FieldRow label={t("pages.logs.modal.iterations")} value={data.iterations} />
          </Grid>
        )}
      </Grid>
    </Paper>
  );
};

const JobDetailModal = ({ open, onClose, job }) => {
  const { t } = useTranslation();

  if (!job) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{ sx: { maxWidth: 1500, width: "100%" } }}
    >
      <DialogTitle>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography variant="h6">
            {job.integration} / {job.type}
          </Typography>
          <Chip
            label={job.status}
            color={getStatusColor(job.status)}
            size="small"
          />
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {/* Basic Info */}
        <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: "bold" }}>
          {t("pages.logs.modal.basicInfo")}
        </Typography>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6}>
            <FieldRow label={t("pages.logs.modal.jobId")} value={job._id} mono />
          </Grid>
          <Grid item xs={6} sm={3}>
            <FieldRow label={t("pages.logs.modal.customerId")} value={job.customerId} />
          </Grid>
          <Grid item xs={6} sm={3}>
            <FieldRow label={t("pages.logs.modal.language")} value={job.language} />
          </Grid>
          <Grid item xs={6} sm={3}>
            <FieldRow label={t("pages.logs.table.status")} value={job.status} />
          </Grid>
          <Grid item xs={6} sm={3}>
            <FieldRow label={t("pages.logs.table.step")} value={job.step} />
          </Grid>
          <Grid item xs={6} sm={3}>
            <FieldRow label={t("pages.logs.table.attempt")} value={job.attempt} />
          </Grid>
          <Grid item xs={6} sm={3}>
            <FieldRow
              label={t("pages.logs.modal.cancelRequested")}
              value={job.cancelRequested ? t("pages.logs.modal.yes") : t("pages.logs.modal.no")}
            />
          </Grid>
        </Grid>

        <Divider sx={{ my: 2 }} />

        {/* Timestamps */}
        <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: "bold" }}>
          {t("pages.logs.modal.timestamps")}
        </Typography>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} sm={4}>
            <FieldRow label={t("pages.logs.table.createdAt")} value={formatDateTime(job.createdAt)} />
          </Grid>
          <Grid item xs={6} sm={4}>
            <FieldRow label={t("pages.logs.modal.updatedAt")} value={formatDateTime(job.updatedAt)} />
          </Grid>
          <Grid item xs={6} sm={4}>
            <FieldRow
              label={t("pages.logs.modal.lastHeartbeat")}
              value={formatDateTime(job.lastHeartbeatAt)}
            />
          </Grid>
        </Grid>

        {/* Error Block */}
        {job.error && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography
              variant="subtitle1"
              sx={{ mb: 2, fontWeight: "bold", color: "error.main" }}
            >
              {t("pages.logs.modal.error")}
            </Typography>
            <Paper
              variant="outlined"
              sx={{ p: 2, mb: 2, backgroundColor: "error.lighter" }}
            >
              <Typography
                variant="body2"
                sx={{ fontFamily: "monospace", whiteSpace: "pre-wrap" }}
              >
                {typeof job.error === "string"
                  ? job.error
                  : job.error.message || JSON.stringify(job.error, null, 2)}
              </Typography>
            </Paper>
          </>
        )}

        {/* Progress Section */}
        {job.progress && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: "bold" }}>
              {t("pages.logs.modal.progress")}
            </Typography>

            {job.progress.message && (
              <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                <Typography variant="body2">{job.progress.message}</Typography>
                {job.progress.finishedAt && (
                  <Typography variant="caption" color="text.secondary">
                    {t("pages.logs.modal.finishedAt")}: {formatDateTime(job.progress.finishedAt)}
                  </Typography>
                )}
              </Paper>
            )}

            <StepBlock title={t("pages.logs.modal.step1")} data={job.progress.step1} t={t} />
            <StepBlock title={t("pages.logs.modal.step2")} data={job.progress.step2} t={t} />
            <StepBlock title={t("pages.logs.modal.step3")} data={job.progress.step3} t={t} />
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t("pages.logs.modal.close")}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default JobDetailModal;
